"use client";

import React, { useCallback, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import {
  Bold, Italic, Strikethrough, Heading2, Quote, Underline as UnderlineIcon,
  Link as LinkIcon, List, ListOrdered, Code, CodeXml, Minus, Highlighter,
  AlignLeft, AlignCenter, AlignRight, File as FileIcon, LoaderCircle,
  RemoveFormatting, Undo, Redo
} from 'lucide-react';
import './TipTap.css';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { cn } from '@/lib/utils';
import { showError } from '@/utils/toast';

interface TipTapEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  isEditable?: boolean; // Új prop a szerkeszthetőség vezérléséhez
  onFileDrop?: (files: File[]) => void;
}

const handleFile = (file: File, editor: Editor, onUploadStart: () => void, onUploadEnd: () => void): Promise<void> => {
  return new Promise((resolve) => {
    if (file.type.startsWith('image/')) {
      onUploadStart();
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const src = e.target?.result as string;
          console.log(`Kép feldolgozása: ${file.name}, src hossza: ${src.length}`);

          // Közvetlenül a ProseMirror view-ba beszúrunk
          const { state, dispatch } = editor.view;
          const node = editor.schema.nodes.image.create({ src });
          const tr = state.tr.insert(state.selection.$anchor.pos, node);
          dispatch(tr);

          console.log(`✅ Kép beillesztve: ${file.name}`);
        } catch (error) {
          console.error('Kép beszúrási hiba:', error);
          showError("Hiba történt a kép beszúrása közben.");
        } finally {
          onUploadEnd();
          resolve();
        }
      };
      reader.onerror = () => {
        showError("Hiba történt a kép beolvasása közben.");
        onUploadEnd();
        resolve();
      };
      reader.readAsDataURL(file);
      return;
    }

    onUploadStart();
    const formData = new FormData();
    formData.append('photo', file);

    (async () => {
      try {
        const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
        const response = await fetch('/api/uploads/temporary_file', {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfToken },
          body: formData,
        });

        if (!response.ok || response.status !== 201) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'A fájl feltöltése sikertelen.');
        }

        const result = await response.json();
        if (result.url) {
          editor
            .chain()
            .focus()
            .insertContent(
              `<a href="${result.url}" data-temp-file="true" data-file-size="${file.size}">${file.name}</a>`
            )
            .run();
          console.log(`✅ Fájl beillesztve: ${file.name}`);
        } else {
          throw new Error('A szerver nem adott vissza URL-t.');
        }
      } catch (error) {
        showError(error instanceof Error ? error.message : 'Ismeretlen hiba történt.');
      } finally {
        onUploadEnd();
        resolve();
      }
    })();
  });
};

interface ToolbarProps {
  editor: Editor | null;
  isUploading: boolean;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  handleUploadStart: () => void;
  handleUploadEnd: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, isUploading, setIsUploading, handleUploadStart, handleUploadEnd }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="tiptap-toolbar sticky top-0 z-10 bg-card border-b border-border flex flex-wrap items-center gap-1 p-2">
      <div className="tiptap-toolbar-group flex gap-1">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`tiptap-toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`}
          title="Félkövér"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`tiptap-toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`}
          title="Dőlt"
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          className={`tiptap-toolbar-button ${editor.isActive('strike') ? 'is-active' : ''}`}
          title="Áthúzott"
        >
          <Strikethrough size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={`tiptap-toolbar-button ${editor.isActive('underline') ? 'is-active' : ''}`}
          title="Aláhúzott"
        >
          <UnderlineIcon size={16} />
        </button>
      </div>

      <div className="tiptap-toolbar-group flex gap-1">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`tiptap-toolbar-button ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
          title="Címsor"
        >
          <Heading2 size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`tiptap-toolbar-button ${editor.isActive('blockquote') ? 'is-active' : ''}`}
          title="Idézet"
        >
          <Quote size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={`tiptap-toolbar-button ${editor.isActive('highlight') ? 'is-active' : ''}`}
          title="Kiemelés"
        >
          <Highlighter size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="tiptap-toolbar-button"
          title="Vízszintes vonal"
        >
          <Minus size={16} />
        </button>
      </div>

      <div className="tiptap-toolbar-group flex gap-1">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`tiptap-toolbar-button ${editor.isActive('bulletList') ? 'is-active' : ''}`}
          title="Felsorolás"
        >
          <List size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`tiptap-toolbar-button ${editor.isActive('orderedList') ? 'is-active' : ''}`}
          title="Számozott lista"
        >
          <ListOrdered size={16} />
        </button>
      </div>

      <div className="tiptap-toolbar-group flex gap-1">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          className={`tiptap-toolbar-button ${editor.isActive('code') ? 'is-active' : ''}`}
          title="Kódrészlet"
        >
          <Code size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`tiptap-toolbar-button ${editor.isActive('codeBlock') ? 'is-active' : ''}`}
          title="Kódblokk"
        >
          <CodeXml size={16} />
        </button>
      </div>
      <div className="tiptap-toolbar-group flex gap-1">
        <button
          type="button"
          onClick={setLink}
          className={`tiptap-toolbar-button ${editor.isActive('link') ? 'is-active' : ''}`}
          title="Link beszúrás"
        >
          <LinkIcon size={16} />
        </button>
      </div>

      <div className="tiptap-toolbar-group flex gap-1">
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`tiptap-toolbar-button ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
          title="Balra igazítás"
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`tiptap-toolbar-button ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
          title="Középre igazítás"
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`tiptap-toolbar-button ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
          title="Jobbra igazítás"
        >
          <AlignRight size={16} />
        </button>
      </div>

      <div className="tiptap-toolbar-group flex gap-1">
        <button
          type="button"
          onClick={() => {
            if (!editor) return;
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '*/*';
            input.multiple = true;
            input.onchange = async () => {
              if (!input.files || input.files.length === 0) return;
              // FileList-et Arrayre konvertálunk
              const filesArray = Array.from(input.files);
              console.log(`📤 "${filesArray.length} fájl kiválasztva`);

              for (const file of filesArray) {
                console.log(`📎 Feldolgozás: ${file.name} (${file.type})`);
                await handleFile(file, editor, handleUploadStart, handleUploadEnd);
              }
            };
            input.click();
          }}
          className="tiptap-toolbar-button"
          title="Fájl vagy kép beillesztése"
        >
          {isUploading ? <LoaderCircle size={16} className="animate-spin" /> : <FileIcon size={16} />}
        </button>
      </div>

      <div className="tiptap-toolbar-group flex gap-1 ml-auto">
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          className="tiptap-toolbar-button"
          title="Formázás törlése"
        >
          <RemoveFormatting size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className="tiptap-toolbar-button" title="Visszavonás"
        ><Undo size={16} /></button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className="tiptap-toolbar-button" title="Ismét"
        ><Redo size={16} /></button>
      </div>
    </div>
  );
};

const TipTapEditor = forwardRef<{ focus: () => void }, TipTapEditorProps>(({ value, onChange, placeholder, className, isEditable = true, onFileDrop }, ref) => {
  const [isUploading, setIsUploading] = useState(false);
  const uploadCountRef = useRef(0);

  const handleUploadStart = () => {
    uploadCountRef.current++;
    setIsUploading(true);
  };

  const handleUploadEnd = () => {
    uploadCountRef.current = Math.max(0, uploadCountRef.current - 1);
    if (uploadCountRef.current === 0) {
      setIsUploading(false);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // A StarterKit alapértelmezetten kezeli a legtöbb funkciót.
        // A link beállításait itt adjuk meg, hogy elkerüljük a duplikációt.
        link: {
          openOnClick: false,
          autolink: true,
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Write something...',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false, // Kattintásra ne nyissa meg a linket szerkesztés közben
        autolink: true,
        HTMLAttributes: {
          // Alapértelmezett attribútumok a linkekhez
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Underline,
      Highlight.configure({ multicolor: true }),
    ],
    content: value,
    editable: isEditable,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm sm:prose lg:prose-lg xl:prose-2xl p-4 focus:outline-none',
      },
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false;

        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;

        if (onFileDrop) {
          onFileDrop(Array.from(files));
          return true;
        }

        const editorInstance = editor;
        if (!editorInstance) return false;

        // Aszinkron feldolgozás az összes fájlra
        (async () => {
          for (const file of Array.from(files)) {
            await handleFile(file, editorInstance, handleUploadStart, handleUploadEnd);
          }
        })();

        return true; // Megakadályozzuk az alapértelmezett böngésző viselkedést
      },
    },
  });

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (editor) {
        editor.chain().focus().run();
      }
    },
  }));

  React.useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const isSame = editor.getHTML() === value;
      if (!isSame) editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  return (
    <div className={cn("tiptap-container flex flex-col border rounded-md shadow-sm max-h-[70vh]", className)}>
      <Toolbar editor={editor} isUploading={isUploading} setIsUploading={setIsUploading} handleUploadStart={handleUploadStart} handleUploadEnd={handleUploadEnd} />
      <div className="tiptap-editor-content flex-1 overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
});

TipTapEditor.displayName = "TipTapEditor";

export default TipTapEditor;
